/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ExclamationTriangleIcon } from '@radix-ui/react-icons';
import { useProperty } from '@/features/property-management/pages/PropertyContext';
import { DataStateError, Text, UnstyledButton } from '@/shared/ui';
import { useIsMobile } from '@/shared/hooks/use-is-mobile';
import { collectImageRefs } from '@/shared/utils/portal-publish';
import { PropertyDrawer } from '../PropertyDrawer';
import type { PropertyDrawerSection } from '../PropertyDrawer/drawerSections';
import { VisitDrawer } from '@/features/visits/components/VisitDrawer';
import { useAuth } from '@context/authentication';
import { PropertyBreadcrumb, PropertyHeaderTop } from './PropertyHeader';
import { PriceStrip } from './PriceStrip';
import { PerformanceStrip } from './PerformanceStrip';
import { PropertyGallery } from './PropertyGallery';
import { PropertyActionsMenu } from './PropertyActionsMenu';
import { MobileActionBar } from './MobileActionBar';
import { getCategoryLabel } from '../PropertyFormDualPane/schema';
import {
  MobileDetailHeader, MobileDetailAction,
} from '@/shared/ui/components/composed/mobile-sheet';
import { Pencil1Icon } from '@radix-ui/react-icons';
import { TabBar, type TabCounts, type TabName } from './TabBar';
import { EntityProposalsSection } from '@/features/deals/components/EntityProposalsSection';
import { SectionCard } from './SectionCard';
import { OverviewTab } from './tabs/OverviewTab';
import { CharacteristicsTab } from './tabs/CharacteristicsTab';
import { VisitsTab } from './tabs/VisitsTab';
import { DocumentsTab } from './tabs/DocumentsTab';
import { PublicationsTab } from './tabs/PublicationsTab';
import { PropertyDetailSidebar } from './PropertyDetailSidebar';
import { usePropertyMetrics } from './hooks/usePropertyMetrics';
import { usePropertyDocuments } from './hooks/usePropertyDocuments';
import { usePropertyVisits } from './hooks/usePropertyVisits';
import { usePropertyImages } from './hooks/usePropertyImages';
import { usePropertyStatusActions } from './hooks/usePropertyStatusActions';
import { usePropertyFavorite } from './hooks/usePropertyFavorite';
import { shareProperty, trackShareEvent } from './shareProperty';
import { PropertyDetailSkeleton } from './PropertyDetailSkeleton';
import { useAgencySiteSlug } from './hooks/useAgencySiteSlug';
import { uploadToPresignedPost } from '@/shared/utils/presigned-upload';
import { uploadPropertyFileImages } from '@/services/images-upload';
import { toast } from 'sonner';

/** Mirrors the backend's presigned-POST content-length-range for property documents. */
const MAX_DOCUMENT_UPLOAD_BYTES = 52428800;

/**
 * Property detail page: gallery and performance up top, six tabs and the
 * right rail below, with the edit drawer behind the header.
 *
 * The screen opens with what the listing *is* (photos, price) and how it is
 * *doing* (views, interested clients, visits, shares, days on market) side by
 * side, because those five figures are what decide whether the price moves —
 * and at the bottom of a sidebar nobody scrolled to, they never did.
 */
export const PropertyDetailView: React.FC = () => {
  const { agency_id: agencyId = '', property_id: propertyId = '' } = useParams();
  const property = useProperty((s) => s.property);
  const refreshFetch = useProperty((s) => s.refreshFetch);
  const loaded = useProperty((s) => s.loaded);
  const error = useProperty((s) => s.error);
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const { t } = useTranslation('common');
  const { t: tProperties } = useTranslation('features/properties');
  const { t: tProposals } = useTranslation('features/deals');
  // Lets a caller (e.g. the "fazer primeira publicação" nudge on the social
  // settings screen) land the user straight on the Publicação tab, with the
  // publish dialog already open, instead of making them find their way here
  // on their own. Captured once and stripped from the URL immediately so a
  // refresh/back-nav doesn't keep reopening the dialog.
  const [{ initialTab, autoOpenPublish }] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab');
    const openPublish = params.get('openPublish') === '1';
    if (tab !== null || params.has('openPublish')) {
      params.delete('tab');
      params.delete('openPublish');
      const newSearch = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (newSearch ? `?${newSearch}` : ''));
    }
    return {
      // TabName doubles as the tab's id; TabBar resolves it to a label key in
      // TAB_LABEL_KEYS, so it is never rendered as-is.
      // i18n-exempt: internal tab id, not display text
      initialTab: (tab === 'publications' ? 'Publicação' : 'Visao Geral') as TabName,
      autoOpenPublish: openPublish,
    };
  });
  const [activeTab, setActiveTab] = useState<TabName>(initialTab);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerSection, setDrawerSection] = useState<PropertyDrawerSection | undefined>();
  const [visitDrawerOpen, setVisitDrawerOpen] = useState(false);
  const [proposalCount, setProposalCount] = useState<number | undefined>(undefined);

  const { metrics, loading: metricsLoading, error: metricsError, refetch: refetchMetrics } =
    usePropertyMetrics(agencyId, propertyId);
  const { documents, loading: docsLoading, error: docsError, getUploadLink, deleteDocument, renameDocument, refetch: refetchDocs } =
    usePropertyDocuments(agencyId, propertyId);
  const { visits, loading: visitsLoading, error: visitsError, refetch: refetchVisits } = usePropertyVisits(agencyId, propertyId);
  const { slug: agencySlug } = useAgencySiteSlug(agencyId);
  const { favorite, toggle: toggleFavorite } = usePropertyFavorite(agencyId, propertyId);

  const fallbackKeys = useMemo(
    () => (property?.media?.images ?? []).map((img) => img.file_key),
    [property?.media?.images],
  );
  const { imageUrls, refetch: refetchImages } = usePropertyImages(
    agencyId,
    propertyId,
    fallbackKeys,
    property?.media?.cover_image,
  );
  const [uploadingPhotos, setUploadingPhotos] = useState(false);

  /*
   * Adding the first photo straight from the empty frame.
   *
   * Same path the editor's uploader takes — presign, WebP, POST to S3 — so
   * the photo lands as a real listing image rather than something only this
   * screen knows about. The list is re-read afterwards because nothing else
   * tells this screen the property now has a cover.
   */
  const handleAddPhotos = useCallback(async (files: File[]) => {
    if (!agencyId || !propertyId || files.length === 0) return;
    setUploadingPhotos(true);
    try {
      await uploadPropertyFileImages(files, propertyId, agencyId);
      refetchImages();
      toast.success(tProperties('detailView.gallery.uploadSuccess', { count: files.length }));
    } catch {
      toast.error(tProperties('detailView.gallery.uploadError'));
    } finally {
      setUploadingPhotos(false);
    }
  }, [agencyId, propertyId, refetchImages, tProperties]);

  const handleBack = useCallback(() => {
    navigate(`/app/agencies/${agencyId}/properties`);
  }, [navigate, agencyId]);
  // OLX "Editar": the ad is the property, so the property is edited and
  // saving updates the existing publications.
  const [editForPublications, setEditForPublications] = useState(false);
  const handleEdit = useCallback(() => { setEditForPublications(false); setDrawerSection(undefined); setDrawerOpen(true); }, []);
  const handleEditForPublications = useCallback(() => { setEditForPublications(true); setDrawerSection(undefined); setDrawerOpen(true); }, []);
  const handleEditSection = useCallback((section: PropertyDrawerSection) => {
    setEditForPublications(false);
    setDrawerSection(section);
    setDrawerOpen(true);
  }, []);
  /* Drives the detail header's shadow; see `MobileDetailHeader`. */
  const [scrolled, setScrolled] = useState(false);

  /*
   * What the header calls this listing.
   *
   * Not the id. `JT6UaDMQWpQ` is a database key: it is the right thing on a
   * card, where it is the short string somebody quotes over the phone, and
   * the wrong thing as the only words at the top of a screen — a person
   * arriving from the list cannot tell whether it is the listing they
   * tapped. The kind and the neighbourhood are what they just read on the
   * card they came from, and they fit. The code is still on the screen, as
   * a chip in the content below.
   *
   * The headline is not an option: these come from the crawler as a full
   * sentence ("Apartamento para comprar com 44 m², 2 quartos...") and would
   * be an ellipsis three words in.
   */
  const headerTitle = [
    getCategoryLabel(property.classification?.category ?? '', tProperties),
    property.location?.neighborhood || property.location?.city,
  ].filter(Boolean).join(' · ') || property.id;
  const handleDrawerClose = useCallback(() => setDrawerOpen(false), []);
  const handleDrawerSuccess = useCallback(() => {
    setDrawerOpen(false);
    refreshFetch();
  }, [refreshFetch]);
  const handleRetry = useCallback(() => refreshFetch(), [refreshFetch]);
  const handleDeleteDocument = useCallback((documentId: string) => {
    deleteDocument(documentId);
  }, [deleteDocument]);
  const handleRenameDocument = useCallback((documentId: string, filename: string) => {
    void renameDocument(documentId, filename);
  }, [renameDocument]);

  const handleShare = useCallback(() => {
    if (!agencySlug) {
      toast.error(tProperties('detailView.share.publicLinkUnavailable'));
      return;
    }
    void shareProperty({
      title: property?.description?.headline || 'Imóvel',
      agencySlug,
      propertySlug: property?.slug,
      propertyId: property?.id,
    });
    void trackShareEvent(agencyId, propertyId).then(() => refetchMetrics());
  }, [property?.description?.headline, agencySlug, property?.slug, property?.id, tProperties, agencyId, propertyId, refetchMetrics]);

  const handleUpload = useCallback(async (file: File) => {
    if (file.size > MAX_DOCUMENT_UPLOAD_BYTES) {
      toast.error(tProperties('detailView.documents.fileTooLarge'));
      return;
    }
    const link = await getUploadLink(file.name, file.type);
    if (!link?.upload_url) {
      toast.error(tProperties('detailView.documents.uploadError'));
      return;
    }
    try {
      await uploadToPresignedPost(link.upload_url, file);
      refetchDocs();
    } catch {
      await deleteDocument(link.document_id);
      toast.error(tProperties('detailView.documents.uploadError'));
    }
  }, [deleteDocument, getUploadLink, refetchDocs, tProperties]);


  const statusActions = usePropertyStatusActions({
    agencyId,
    propertyId,
    onChanged: refreshFetch,
    onRemoved: handleBack,
  });

  const handleVisitCreated = useCallback(() => {
    setVisitDrawerOpen(false);
    refetchVisits();
    refetchMetrics();
  }, [refetchVisits, refetchMetrics]);

  if (loaded && error && !property?.id) {
    return (
      <div className="flex-1 min-h-0 flex items-center justify-center p-[var(--space-5)]" data-testid="property-detail-error">
        <div className="flex flex-col items-center gap-[var(--space-4)]">
          <DataStateError
            icon={<Text component="span" className="flex text-(--status-error)"><ExclamationTriangleIcon width={28} height={28} /></Text>}
            message={t('messages.error.loadFailed')}
            onRetry={handleRetry}
            retryLabel={t('messages.error.retry')}
            data-testid="property-detail-error-visual"
          />
          <UnstyledButton
            type="button"
            data-testid="property-detail-error-back"
            onClick={handleBack}
            className="text-xs font-medium text-(--text-secondary) underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--seed-base) focus-visible:ring-offset-1 rounded-(--radius-md) px-(--space-2) py-(--space-1)"
          >
            {t('buttons.back')}
          </UnstyledButton>
        </div>
      </div>
    );
  }

  if (!property || !property.id) return <PropertyDetailSkeleton />;

  const counts: TabCounts = {
    Visitas: visits.length,
    Propostas: proposalCount,
    Documentos: documents.length,
  };

  const menuProps = {
    status: property.classification?.status ?? 'active',
    saving: statusActions.saving,
    onSuspend: statusActions.suspend,
    onMarkSold: statusActions.markSold,
    onArchive: statusActions.archive,
    onReactivate: statusActions.reactivate,
    onDelete: statusActions.remove,
  };
  const actionsMenu = <PropertyActionsMenu {...menuProps} />;
  /* The same menu, sized for the header's row of 44px controls. */
  const mobileActionsMenu = <PropertyActionsMenu {...menuProps} inHeader />;

  const sidebar = (
    <PropertyDetailSidebar
      property={property}
      agencyId={agencyId}
      propertyId={propertyId}
      onBrokersSaved={refreshFetch}
      onEditSection={handleEditSection}
    />
  );

  return (
    // The app shell pins html/body at height:100%; overflow:hidden, so the
    // page never scrolls -- the scrolling belongs to a container. The shell
    // hands this screen a block-level box that is already viewport-height
    // and already scrolls, which is why `h-full` and not `flex-1`: inside a
    // block parent `flex:1` does nothing, the root grows to content height,
    // and the action bar rides to the bottom of a 2800px column instead of
    // sitting on the fold. Bounded to the shell's box, the middle column
    // becomes the scroller and the bar is a flex:none sibling above it --
    // no `position: fixed` needed, which would anchor to a viewport this
    // content does not scroll within anyway.
    <div className="h-full min-h-0 flex flex-col overflow-hidden bg-[var(--surface-primary)]">
      {/*
        The screen's own bar, in place of the shell's. `flex: none` above the
        scrolling box, so it stays put without `position: fixed` — which would
        anchor to a viewport this content does not scroll within anyway.
      */}
      {isMobile && (
        <MobileDetailHeader
          title={headerTitle}
          onBack={handleBack}
          backLabel={tProperties('detailView.breadcrumb')}
          scrolled={scrolled}
          actions={(
            <>
              <MobileDetailAction
                label={tProperties('detailView.actions.edit')}
                testId="property-header-edit"
                onClick={handleEdit}
              >
                <Pencil1Icon width={18} height={18} />
              </MobileDetailAction>
              {mobileActionsMenu}
            </>
          )}
        />
      )}
      <div
        className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-[var(--space-4)] p-[var(--space-5)]"
        data-testid="property-detail-view"
        onScroll={(e) => {
          const next = e.currentTarget.scrollTop > 4;
          setScrolled((current) => (current === next ? current : next));
        }}
      >
        {/* The header above carries the way back on a phone. */}
        {!isMobile && <PropertyBreadcrumb property={property} onBack={handleBack} />}

        <div className="grid items-start gap-[var(--space-5)] md:grid-cols-[minmax(0,420px)_minmax(0,1fr)]">
          <PropertyGallery
            images={imageUrls}
            onAddPhotos={handleAddPhotos}
            uploading={uploadingPhotos}
          />
          <div className="flex min-w-0 flex-col gap-[var(--space-4)]">
            <PropertyHeaderTop
              property={property}
              onBack={handleBack}
              onEdit={handleEdit}
              onShare={handleShare}
              onSchedule={isMobile ? undefined : () => setVisitDrawerOpen(true)}
              actionsMenu={isMobile ? undefined : actionsMenu}
              favorite={favorite}
              onToggleFavorite={toggleFavorite}
            />
            <PriceStrip property={property} />
            <PerformanceStrip
              metrics={metrics}
              loading={metricsLoading}
              error={metricsError}
              onOpenVisits={() => setActiveTab('Visitas')}
              onOpenProposals={() => setActiveTab('Propostas')}
              onOpenPublications={() => setActiveTab('Publicação')}
              onShare={handleShare}
            />
            {/* The tabs close this column rather than opening the one below.
                The header block runs shorter than the gallery beside it, and
                the strip of dead space that left — between the performance
                figures and the first sidebar card — was the largest empty
                area on the screen. */}
            {/*
              On a phone the tab bar is hoisted out of this column and
              rendered as a direct child of the scrolling box below. A sticky
              element can only travel inside its own containing block, and
              this column is 557px of a 2341px scroll — pinned here, the tabs
              slid away with the column the moment it passed.
            */}
            {!isMobile && (
              <TabBar activeTab={activeTab} onTabChange={setActiveTab} counts={counts} />
            )}
          </div>
        </div>

        {isMobile && (
          <TabBar activeTab={activeTab} onTabChange={setActiveTab} counts={counts} />
        )}

        <div className="grid items-start gap-[var(--space-5)] md:grid-cols-[minmax(0,1fr)_minmax(260px,340px)]">
          <div className="flex min-w-0 flex-col gap-[var(--space-4)]">
            {activeTab === 'Visao Geral' && <OverviewTab property={property} agencyId={agencyId} propertyId={propertyId} onEditSection={handleEditSection} />}
            {activeTab === 'Caracteristicas' && <CharacteristicsTab property={property} onEditSection={handleEditSection} />}
            {activeTab === 'Visitas' && (
              <VisitsTab
                visits={visits}
                loading={visitsLoading}
                error={visitsError}
                onSchedule={() => setVisitDrawerOpen(true)}
              />
            )}
            {/* Mounted whatever tab is showing, and hidden rather than
                unmounted: the tab strip states how many offers the listing
                has, and a count that only appears once you have already
                opened the tab tells you nothing you did not just read. */}
            <div hidden={activeTab !== 'Propostas'}>
              <SectionCard
                data-testid="property-proposals"
                title={tProposals('detail.proposals.title')}
                titleAccessory={
                  <Text
                    component="span"
                    className="whitespace-nowrap text-[length:var(--text-meta)] text-[var(--text-tertiary-soft)]"
                  >
                    {tProperties('detailView.proposals.readOnly')}
                  </Text>
                }
              >
                <EntityProposalsSection
                  agencyId={agencyId}
                  propertyId={propertyId}
                  onCountChange={setProposalCount}
                />
              </SectionCard>
            </div>
            {activeTab === 'Documentos' && (
              <DocumentsTab
                documents={documents}
                loading={docsLoading}
                error={docsError}
                onUpload={handleUpload}
                onDelete={handleDeleteDocument}
                onRename={handleRenameDocument}
                documentation={property.documentation}
              />
            )}
            {activeTab === 'Publicação' && (
              <PublicationsTab
                propertyId={propertyId}
                agencyId={agencyId}
                propertyImages={collectImageRefs(property.media?.images)}
                autoOpenPublish={autoOpenPublish}
                onEditProperty={handleEditForPublications}
              />
            )}
          </div>
          {sidebar}
        </div>
      </div>

      {isMobile && (
        <MobileActionBar
          onSchedule={() => setVisitDrawerOpen(true)}
          onShare={handleShare}
        />
      )}

      <PropertyDrawer
        open={drawerOpen}
        onClose={handleDrawerClose}
        agencyId={agencyId}
        editingProperty={property as unknown as Record<string, any>}
        onSuccess={handleDrawerSuccess}
        syncPublications={editForPublications}
        focusSection={drawerSection}
      />

      <VisitDrawer
        open={visitDrawerOpen}
        onClose={() => setVisitDrawerOpen(false)}
        agencyId={agencyId}
        userEmail={user?.profile?.email ?? ''}
        initialPropertyId={propertyId}
        onSuccess={handleVisitCreated}
      />
    </div>
  );
};

export default PropertyDetailView;
